import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Textarea, ActionIcon, Tooltip, Group } from '@mantine/core';
import { IconCornerDownRight, IconTrash } from '@tabler/icons-react';

/** Ширина одного уровня вложенности. Ею же меряется горизонтальный сдвиг при
 *  перетаскивании, поэтому значение общее для строки и для списка. */
export const INDENT_PX = 24;

interface Props {
  node: { id: string; text: string };
  depth: number;
  readOnly?: boolean;
  /** When false the node is a flat item: no "add sub-item" and no nesting. */
  allowChildren?: boolean;
  onChangeText: (id: string, text: string) => void;
  onAddChild: (id: string) => void;
  onRemove: (id: string) => void;
  /** Enter в поле: добавить пункт того же уровня следом за этим. */
  onAddSibling: (id: string) => void;
  /** Id только что добавленного узла — он получает фокус при монтировании. */
  focusId?: string | null;
  /** Ручка перетаскивания. Рисует её список — он же владеет drag-состоянием. */
  dragHandle?: React.ReactNode;
}

// Одна строка секции эссе. Вложенность больше не выражается вложенным
// рендером: список разворачивает дерево в плоский набор строк (иначе не
// посчитать позицию при перетаскивании), а уровень показывается отступом и
// линией слева.
export const EssayTreeNode: React.FC<Props> = ({
  node,
  depth,
  readOnly,
  allowChildren = true,
  onChangeText,
  onAddChild,
  onRemove,
  onAddSibling,
  focusId,
  dragHandle,
}) => {
  const { t } = useTranslation();
  const isGroup = allowChildren && depth === 0;
  return (
    <Box
      style={{
        display: 'flex',
        alignItems: 'stretch',
        marginTop: 'var(--mantine-spacing-xs)',
      }}
    >
      {/* По линии на каждого предка. Пока строки рендерились вложенно, рамки
          родительских блоков рисовали эту лесенку сами; в плоском списке
          вложенность больше ничем не видна, поэтому линии рисуем явно. */}
      {Array.from({ length: depth }, (_, i) => (
        <Box
          key={i}
          style={{
            width: INDENT_PX,
            flexShrink: 0,
            borderLeft: '2px solid var(--mantine-color-default-border)',
          }}
        />
      ))}
      <Group
        gap="xs"
        wrap="nowrap"
        align="flex-start"
        style={{
          flex: 1,
          minWidth: 0,
          paddingLeft: depth === 0 ? 0 : 'var(--mantine-spacing-xs)',
        }}
      >
        {dragHandle}
        {/* Слева, на линии вложенности, и не плюсом: плюс внизу карточки
            добавляет пункт того же уровня, а эта кнопка — вложенный под ним. */}
        {!readOnly && allowChildren && (
          <Tooltip label={t('essays.addSubItem')} withArrow>
            <ActionIcon
              variant="subtle"
              color="teal"
              mt={4}
              aria-label={t('essays.addSubItem')}
              onClick={() => onAddChild(node.id)}
            >
              <IconCornerDownRight size={16} />
            </ActionIcon>
          </Tooltip>
        )}
        <Textarea
          autosize
          minRows={1}
          maxRows={8}
          flex={1}
          readOnly={readOnly}
          value={node.text}
          placeholder={isGroup ? t('essays.groupPlaceholder') : t('essays.itemPlaceholder')}
          autoFocus={focusId === node.id}
          onChange={(e) => onChangeText(node.id, e.currentTarget.value)}
          onKeyDown={(e) => {
            // Enter — новый пункт, Shift+Enter — перенос строки внутри пункта.
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              onAddSibling(node.id);
            }
          }}
        />
        {!readOnly && (
          <Tooltip label={t('common.delete')} withArrow>
            <ActionIcon
              variant="subtle"
              color="red"
              mt={4}
              aria-label={t('common.delete')}
              onClick={() => onRemove(node.id)}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
    </Box>
  );
};
